import { useMemo, type RefObject } from "react";
import { Deferred, WhenVisible } from "@inertiajs/react";
import { SearchResultCard } from "@components/job_search/search_result_card.tsx";
import { ExpandSearchCard } from "@components/job_search/expand_search_card.tsx";
import useTranslation from "@hooks/use_translation.ts";
import useSearchResultsReload from "@hooks/use_search_results_reload.ts";
import useSubscriptionModal from "@hooks/use_subscription_modal.ts";
import { newJobAlertPath } from "@/utils/new_job_alert_path.ts";
import type { JobPost, SearchParams } from "@/types/job_search/search";

const LIST_SKELETON_COUNT = 6;

type Props = {
  jobPosts: JobPost[] | undefined;
  browsing: boolean;
  moreResultsAvailable: boolean;
  currentPage: number;
  lastPublishDate: string | null;
  searchParams: SearchParams;
  createTrackSearchResultClick: (post: JobPost, position: number) => () => void;
  onView: (id: string) => void;
  onSelect: (post: JobPost, position: number) => void;
  selectedId: string | null;
  savedJobsEnabled: boolean;
  scrollRef: RefObject<HTMLDivElement | null>;
};

type ListProps = Pick<
  Props,
  | "jobPosts"
  | "moreResultsAvailable"
  | "createTrackSearchResultClick"
  | "onView"
  | "onSelect"
  | "selectedId"
  | "savedJobsEnabled"
> & { jobPosts: JobPost[] };

const ResultList = ({
  jobPosts,
  moreResultsAvailable,
  createTrackSearchResultClick,
  onView,
  onSelect,
  selectedId,
  savedJobsEnabled,
}: ListProps) => {
  const { openModal } = useSubscriptionModal();

  const resultCards = useMemo(() => {
    const jobPostCards = jobPosts.map((post, index) => (
      <SearchResultCard
        key={post.id}
        id={post.id}
        title={post.title}
        company={post.companyName}
        logoUrl={post.logoUrl}
        publishedAt={post.firstPublished}
        url={post.viewJobPath ?? post.publicUrl}
        external={!post.viewJobPath}
        locations={post.locations}
        workType={post.workType}
        payRange={post.payRanges}
        summary={post.summary}
        description={post.description}
        trackClick={createTrackSearchResultClick(post, index + 1)}
        viewed={post.viewed}
        onView={onView}
        appliedAt={post.appliedAt}
        onSave={savedJobsEnabled ? () => {} : undefined}
        onCreateAlert={() => openModal(newJobAlertPath(post))}
        onSelect={() => onSelect(post, index + 1)}
        selected={post.id === selectedId}
      />
    ));

    if (jobPosts.length > 0 && !moreResultsAvailable) {
      jobPostCards.push(<ExpandSearchCard key="expand-search-card" />);
    }

    return jobPostCards;
  }, [
    jobPosts,
    createTrackSearchResultClick,
    onView,
    onSelect,
    selectedId,
    savedJobsEnabled,
    openModal,
  ]);

  return <>{resultCards}</>;
};

const LoadingResults = () => (
  <>
    {[...Array(LIST_SKELETON_COUNT)].map((_, i) => (
      <SearchResultCard
        key={i}
        id={i.toString()}
        title={""}
        company={""}
        logoUrl={""}
        publishedAt={""}
        url={""}
        loading={true}
        viewed={false}
        onView={() => {}}
        appliedAt={null}
      />
    ))}
  </>
);

export const JobSearchResultsColumn = ({
  jobPosts,
  browsing,
  moreResultsAvailable,
  currentPage,
  lastPublishDate,
  searchParams,
  createTrackSearchResultClick,
  onView,
  onSelect,
  selectedId,
  savedJobsEnabled,
  scrollRef,
}: Props) => {
  const { t } = useTranslation("jobSearch");
  const { reloadParams, loading } = useSearchResultsReload({
    browsing,
    currentPage,
    lastPublishDate,
    searchParams,
  });

  return (
    <div className="flex flex-col h-full w-full min-[900px]:w-[430px] min-[900px]:min-w-[430px] min-[900px]:shrink-0 min-[900px]:overflow-hidden bg-white rounded-lg">
      <div
        ref={scrollRef}
        className="flex-1 min-h-0 min-[900px]:overflow-auto flex flex-col gap-4 p-4"
      >
        <Deferred fallback={<LoadingResults />} data={"jobPosts"}>
          <ResultList
            jobPosts={jobPosts!}
            moreResultsAvailable={moreResultsAvailable}
            createTrackSearchResultClick={createTrackSearchResultClick}
            onView={onView}
            onSelect={onSelect}
            selectedId={selectedId}
            savedJobsEnabled={savedJobsEnabled}
          />
        </Deferred>
        {loading && <LoadingResults />}
        {moreResultsAvailable && (
          <WhenVisible fallback={t("loading")} params={reloadParams} always>
            <div></div>
          </WhenVisible>
        )}
      </div>
    </div>
  );
};

export default JobSearchResultsColumn;
